/**
 * Talking to the Omni-Torn extension, when it's installed in this browser. Its small helper on
 * this site (extension content/site-bridge.ts) answers messages posted on this page; it answers
 * only this site (the server the extension is signed in to) and never hands over the key itself:
 *  - hello:  is it there, and is it signed in?
 *  - signin: a sign-in token for this site (after you click "Sign in with the extension");
 *  - torn:   one Torn API read, made by the extension with its key (only the reads this site uses);
 *  - prefs:  "your settings changed here" — the extension picks them up now instead of later;
 *  - npc:    Loot Rangers' NPC attack plan, as the extension already keeps it (lzpt.io; nothing of yours involved).
 *  - ffs-targets: FF Scouter's target list through the extension's own FF Scouter connection (the Target finder's
 *    backup when Omni-Torn's database answers too few) — it stays on this page, never sent to the server.
 *  - bazaar: an item's bazaar listings from weav3r.dev through the extension's own weav3r connection (the Market page) —
 *    they stay on this page, never sent to the server or the player database.
 */
import type { FfsFilters, FfsTarget, KeyAccess } from '@omni-torn/shared';

export const SITE_CHANNEL = 'omni-torn-site';

export interface ExtHello {
  version: string;
  signedIn: boolean;
  playerId: number | null;
  name: string | null;
  /** Whether it shares with your faction itself (member sharing usable at your level). */
  canShare?: boolean;
}

export interface ExtSignIn {
  token: string;
  until: number;
  playerId: number;
  name: string;
  owner: boolean;
  key: KeyAccess | null;
}

let seq = 0;
const waiting = new Map<number, (reply: unknown) => void>();

window.addEventListener('message', (e: MessageEvent) => {
  if (e.source !== window || e.origin !== location.origin) return;
  const d = e.data as { channel?: unknown; dir?: unknown; id?: unknown; reply?: unknown } | null;
  if (d?.channel !== SITE_CHANNEL || d.dir !== 'to-page' || typeof d.id !== 'number') return;
  waiting.get(d.id)?.(d.reply);
  waiting.delete(d.id);
});

function ask<T>(msg: Record<string, unknown>, timeoutMs: number): Promise<T | null> {
  const id = ++seq;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      waiting.delete(id);
      resolve(null);
    }, timeoutMs);
    waiting.set(id, (reply) => {
      clearTimeout(timer);
      resolve(reply as T);
    });
    window.postMessage({ channel: SITE_CHANNEL, dir: 'to-ext', id, msg }, location.origin);
  });
}

let hello: Promise<ExtHello | null> | null = null;

/** The extension, if it's here (asked a few times while its helper starts). */
export function extension(force = false): Promise<ExtHello | null> {
  if (force) hello = null;
  return (hello ??= (async () => {
    for (let i = 0; i < 4; i++) {
      const r = await ask<ExtHello>({ type: 'hello' }, 250 + i * 250);
      if (r && typeof r.version === 'string') return r;
    }
    return null;
  })());
}

/** `refused`: the sign-in the server just turned down (the extension then gets a fresh one). */
/** `fresh`: sign in with the key again (so the server gets it for background tracking). */
export async function extensionSignIn(refused?: string, fresh = false): Promise<ExtSignIn | { problem: string }> {
  const r = await ask<ExtSignIn | { problem: string }>({ type: 'signin', ...(refused ? { refused } : {}), ...(fresh ? { fresh: true } : {}) }, 20_000);
  return r ?? { problem: 'The extension didn’t answer — is it signed in? (Open its options page.)' };
}

/** `playerId`: who this site is signed in as — the extension refuses if its key is someone else's now. */
export async function extensionTorn(path: string, params: Record<string, string>, playerId: number): Promise<{ body?: unknown; error?: { code: number; message: string } } | null> {
  return ask({ type: 'torn', path, params, playerId }, 30_000);
}

/** Loot Rangers' attack plan as the extension keeps it (its `{ plan, problem, at }` answer), or null without one. */
export async function extensionNpcPlan(): Promise<unknown> {
  if (!(await extension())) return null;
  return ask({ type: 'npc' }, 15_000);
}

export function extensionPrefsChanged(): void {
  void ask({ type: 'prefs' }, 2_000);
}

/** FF Scouter's target list through the extension (null: no extension here, or it didn't answer). */
export async function extensionFfsTargets(filters: FfsFilters, playerId: number): Promise<{ targets: FfsTarget[]; at: number } | { problem: string } | null> {
  if (!(await extension())) return null;
  return ask({ type: 'ffs-targets', filters, playerId }, 30_000);
}

/** An item's bazaar listings from weav3r.dev through the extension (null: no extension here, or it didn't answer). */
export async function extensionBazaar(itemId: number, playerId: number): Promise<unknown> {
  if (!(await extension())) return null;
  return ask({ type: 'bazaar', itemId, playerId }, 20_000);
}
